import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import Login from './Login'
import Dashboard from './Dashboard'
import BlogList from './BlogList'
import BlogEditor from './BlogEditor'
import Keywords from './Keywords'
import { useToast, ToastContainer } from './useToast'

function IconGrid() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> }
function IconFile() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> }
function IconPlus() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> }
function IconSearch() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg> }
function IconLogout() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg> }

export default function App() {
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [page, setPage] = useState('dashboard')
  const [editingId, setEditingId] = useState(null)
  const { toasts, addToast } = useToast()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setAuthLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  function navigate(p, id = null) {
    setPage(p)
    setEditingId(id)
    window.scrollTo(0, 0)
  }

  async function logout() {
    await supabase.auth.signOut()
    setUser(null)
  }

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2.5 }} />
      </div>
    )
  }

  if (!user) return <Login onLogin={setUser} />

  const initials = user.email?.substring(0, 2).toUpperCase() || 'AD'
  const pageTitle = {
    dashboard: 'Dashboard',
    blogs: 'All Blogs',
    new: 'New Blog',
    edit: 'Edit Blog',
    keywords: 'Keyword Queue',
  }[page] || 'Dashboard'

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <IconGrid /> },
    { key: 'blogs', label: 'All Blogs', icon: <IconFile /> },
    { key: 'new', label: 'New Blog', icon: <IconPlus /> },
    { key: 'keywords', label: 'Keyword Queue', icon: <IconSearch /> },
  ]

  return (
    <div className="shell">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <span className="logo-name">Cobro</span>
          <span className="logo-sub">Blog Admin</span>
        </div>
        <nav className="nav">
          {navItems.map(item => (
            <button
              key={item.key}
              className={`nav-item ${page === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.key)}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button className="user-pill" onClick={logout}>
            <div className="avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name" style={{ fontSize: 12 }}>{user.email?.split('@')[0]}</span>
              <span className="user-role">Sign out</span>
            </div>
            <IconLogout />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main">
        <header className="topbar">
          <div className="topbar-left">
            <span className="page-title">{pageTitle}</span>
          </div>
          <div className="topbar-right">
            {page !== 'new' && (
              <button className="btn btn-accent btn-sm" onClick={() => navigate('new')}>
                <IconPlus /> New Blog
              </button>
            )}
            <a
              href="https://thecobro.com/blog"
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-sm"
              style={{ textDecoration: 'none' }}
            >
              View Live Blog ↗
            </a>
          </div>
        </header>

        {page === 'dashboard' && <Dashboard onNavigate={navigate} />}
        {page === 'blogs' && <BlogList onNavigate={navigate} addToast={addToast} />}
        {page === 'new' && <BlogEditor blogId={null} onNavigate={navigate} addToast={addToast} />}
        {page === 'edit' && <BlogEditor blogId={editingId} onNavigate={navigate} addToast={addToast} />}
        {page === 'keywords' && <Keywords onNavigate={navigate} addToast={addToast} />}
      </main>

      <ToastContainer toasts={toasts} />
    </div>
  )
}
